import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import { isEditing, showPreFilledAddressNotice } from '../reducers/checkoutShippingReducer';
import { connect } from 'react-redux';
import { isLoggedIn } from '../helpers/checkoutUser';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import { getRegistrationErrors, getAddressErrors } from '../helpers/checkoutForms';
import ctaStyles from '../components/shared/styles/checkoutCTAButton.scss';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import { getGuestUserId } from '../helpers/checkoutGuestUserToken';

// components
import { ControlledAddressForm } from 'dibs-address-form';
import { CheckoutRegistrationForm } from '../components/forms/CheckoutRegistrationForm';
import { CheckoutShippingTaxWarning } from '../components/shared/CheckoutShippingTaxWarning';

import CheckoutUpdateAction from '../components/shared/actions/CheckoutUpdateAction';
import CheckoutSaveAndContinueAction from '../components/shared/actions/CheckoutSaveAndContinueAction';

import styles from './styles/CheckoutShippingForm.scss';

const CheckoutShippingForm = props => {
    const {
        viewer: viewerRef,
        buyer: buyerRef,
        newShippingForm,
        editShippingForm,
        registrationForm,
        registrationErrors = {},
        wrapperClass,
        showNotice,
        isEditingShipping,
        onChange,
        onBlur,
        isSavingAddress,
        showTaxWarning,
        errorCode,
        showAddressFormErrors,
        isSelectedAddressValid,
        isComputing,
    } = props;

    const [showRegistrationFormErrors, setshowRegistrationFormErrors] = useState(false);

    const viewer = useFragment(
        graphql`
            fragment CheckoutShippingForm_viewer on Viewer {
                ...CheckoutRegistrationForm_viewer
                ...ControlledAddressForm_viewer
                __typename
                enableCheckoutAddressAutocomplete: featureFlag(
                    feature: "checkoutAddressAutocomplete"
                )
            }
        `,
        viewerRef
    );
    const buyer = useFragment(
        graphql`
            fragment CheckoutShippingForm_buyer on User {
                ...CheckoutRegistrationForm_buyer
            }
        `,
        buyerRef
    );
    const isGuestUser = !!getGuestUserId();

    const validation = () => {
        const addressForm = isEditingShipping ? editShippingForm : newShippingForm;
        const { isValid: isValidAddress, errors: addressErrors } = getAddressErrors(addressForm);
        //TODO ENGAGE-1259 once checkout disabler is removed we are no longer registering users inline
        let isValidRegistrationForm;
        let registrationFormErrors = {};

        if ((isLoggedIn() && !isEmailOnly(document.cookie)) || isGuestUser) {
            isValidRegistrationForm = true;
            registrationFormErrors = {};
        } else {
            const registrationFormValidation = getRegistrationErrors({
                form: registrationForm,
            });
            isValidRegistrationForm = registrationFormValidation.isValid;
            registrationFormErrors = registrationFormValidation.errors;
        }

        return {
            isValid: isValidAddress && isValidRegistrationForm,
            errors: { ...addressErrors, ...registrationFormErrors },
        };
    };

    const onSubmit = ({ isValid, errors }) => {
        if (!isValid) {
            setshowRegistrationFormErrors(true);
        }
        props.onSubmit({ isValid, errors });
    };

    const { isMobile } = useContext(CheckoutServerVarsContext);
    const { enableCheckoutAddressAutocomplete } = viewer || {};

    const submitClasses = classNames(ctaStyles.ssoButtonNew, wrapperClass);
    const { isValid, errors = {} } = validation();
    const allErrors = { ...errors, ...registrationErrors };

    return (
        <div
            className={classNames(isMobile ? styles.mobileFormWrapper : styles.shippingFormWrapper)}
        >
            {showNotice && (
                <p className={styles.notice}>
                    <FormattedMessage
                        id="checkout.CheckoutShippingForm.addressNotice"
                        defaultMessage="The city, state, country and postal code below were provided when your custom shipping quote was requested. Please note that if you change any of that information your custom quote will no longer apply."
                    />
                </p>
            )}
            <ControlledAddressForm
                viewer={viewer}
                address={isEditingShipping ? editShippingForm : newShippingForm}
                theme={{ submit: submitClasses, labelClass: styles.label }}
                submitLabel={
                    isEditingShipping ? <CheckoutUpdateAction /> : <CheckoutSaveAndContinueAction />
                }
                onUpdate={onChange}
                onBlur={onBlur}
                onSubmit={({ isValid: isValidAddressForm, errors: addressFormErrors }) => {
                    onSubmit({
                        isValid: isValid && isValidAddressForm,
                        errors: { ...errors, ...addressFormErrors },
                    });
                }}
                isSaving={isSavingAddress}
                isEditing={isEditingShipping && isSelectedAddressValid}
                disableForm={!!errorCode}
                disableSubmit={!!errorCode || isComputing}
                hasRegistrationFormErrors={!isValid && showRegistrationFormErrors}
                showAllErrors={showAddressFormErrors}
                isMobile={isMobile}
                enableAddressAutocomplete={enableCheckoutAddressAutocomplete}
                isNewCheckout
            >
                {(!isLoggedIn() || isEmailOnly(document.cookie)) && !isGuestUser && (
                    <CheckoutRegistrationForm
                        viewer={viewer}
                        buyer={buyer}
                        form={registrationForm}
                        errors={allErrors}
                        showErrors={showRegistrationFormErrors}
                        onChange={onChange}
                    />
                )}
            </ControlledAddressForm>
            {isEditingShipping && showTaxWarning && <CheckoutShippingTaxWarning />}
        </div>
    );
};

CheckoutShippingForm.propTypes = {
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object,
    newShippingForm: PropTypes.object.isRequired,
    editShippingForm: PropTypes.object.isRequired,
    registrationForm: PropTypes.object.isRequired,
    registrationErrors: PropTypes.object.isRequired,
    wrapperClass: PropTypes.string,
    showNotice: PropTypes.bool.isRequired,
    isEditingShipping: PropTypes.bool.isRequired,
    isComputing: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isSavingAddress: PropTypes.bool,
    showTaxWarning: PropTypes.bool,
    errorCode: PropTypes.string,
    showAddressFormErrors: PropTypes.bool,
    isSelectedAddressValid: PropTypes.bool,
};

export default connect(({ shipping, ui }) => ({
    isEditingShipping: isEditing(shipping),
    newShippingForm: shipping.newShippingForm,
    editShippingForm: shipping.editShippingForm,
    registrationForm: shipping.registrationForm,
    registrationErrors: shipping.registrationErrors,
    showTaxWarning: shipping.showTaxWarning,
    showNotice: showPreFilledAddressNotice(shipping),
    isSavingAddress: ui.isSavingAddress,
    errorCode: ui.errorCode,
    showAddressFormErrors: shipping.showAddressFormErrors,
    isSelectedAddressValid: shipping.isSelectedAddressValid,
}))(CheckoutShippingForm);
